import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Ctx = createContext(null);
const LS_KEY = "groot_active_loc";

// G10 — Oturum boyunca aktif depo/mağaza. Üst barda gösterilir, geçişte anında değişir.
export function ActiveLocationProvider({ children }) {
  const { user, isPrivileged } = useAuth();
  const [locations, setLocations] = useState([]);
  const [activeLocId, setActiveLocId] = useState(() => localStorage.getItem(LS_KEY) || "");

  useEffect(() => {
    if (!user) { setLocations([]); return; }
    if (isPrivileged) {
      api.get("/locations").then((r) => setLocations((r.data || []).filter((l) => l.is_active !== false))).catch(() => {});
    } else {
      setLocations((user.locations || []).map((l) => ({ id: l.id, name: l.name })));
    }
  }, [user, isPrivileged]);

  // Varsayılan: geçerli seçim korunur; yoksa (global) kayıtlı ya da "Tümü", (diğer) birincil lokasyon.
  useEffect(() => {
    if (!user) return;
    setActiveLocId((cur) => {
      if (cur && locations.some((l) => l.id === cur)) return cur;
      if (isPrivileged) {
        const stored = localStorage.getItem(LS_KEY);
        if (stored && locations.some((l) => l.id === stored)) return stored;
        return "";
      }
      return user.location_id || user.locations?.[0]?.id || "";
    });
  }, [user, isPrivileged, locations]);

  const setActive = useCallback((id) => {
    const v = id || "";
    setActiveLocId(v);
    if (v) localStorage.setItem(LS_KEY, v); else localStorage.removeItem(LS_KEY);
    try { window.dispatchEvent(new CustomEvent("groot:active-location", { detail: { id: v } })); } catch (e) { /* noop */ }
  }, []);

  const activeLoc = useMemo(() => locations.find((l) => l.id === activeLocId) || null, [locations, activeLocId]);

  return (
    <Ctx.Provider value={{ activeLocId, activeLoc, locations, isGlobal: isPrivileged, setActive }}>
      {children}
    </Ctx.Provider>
  );
}

export const useActiveLocation = () =>
  useContext(Ctx) || { activeLocId: "", activeLoc: null, locations: [], isGlobal: false, setActive: () => {} };
